/**
 * News illustration fallback helper.
 *
 * When a news article has no image_url, we render a category-appropriate
 * static SVG illustration from /public/news-fallbacks/*.svg instead of a
 * lonely icon on a cream background.
 *
 * Phase 2 (admin-uploaded library): the backend may attach
 * `image_fallback_url` to each article — if present we honour that first,
 * and only fall back to these static assets if the DB library is empty
 * or the article doesn't match any tagged illustration.
 */
const MAP = {
  world: "/news-fallbacks/world.svg",
  politics: "/news-fallbacks/politics.svg",
  business: "/news-fallbacks/business.svg",
  technology: "/news-fallbacks/technology.svg",
  science: "/news-fallbacks/science.svg",
  health: "/news-fallbacks/health.svg",
  environment: "/news-fallbacks/environment.svg",
  entertainment: "/news-fallbacks/entertainment.svg",
  sports: "/news-fallbacks/sports.svg",
  "good news": "/news-fallbacks/good-news.svg",
  "odd news": "/news-fallbacks/odd-news.svg",
  weather: "/news-fallbacks/weather.svg",
  war: "/news-fallbacks/war.svg",
  "war & conflict": "/news-fallbacks/war.svg",
  disasters: "/news-fallbacks/disasters.svg",
  "news you can use": "/news-fallbacks/news-you-can-use.svg",
};

const DEFAULT = "/news-fallbacks/default.svg";

/** Prefix a relative /api/... URL with REACT_APP_BACKEND_URL so <img> loads work. */
function resolveApiUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return base.replace(/\/$/, "") + url;
  }
  return url;
}

/**
 * Returns the best image URL for a news article:
 *   1. Editor-supplied image_url (as-is).
 *   2. Backend-attached image_fallback_url (from the admin illustration library).
 *   3. Curated static SVG by category.
 *   4. Neutral folded-newspaper default.
 */
export function newsImageFor(article) {
  if (!article) return DEFAULT;
  if (article.image_url) return article.image_url;
  if (article.image_fallback_url) return resolveApiUrl(article.image_fallback_url);
  const key = (article.category || "").trim().toLowerCase();
  return MAP[key] || DEFAULT;
}

/** True if we're rendering a fallback illustration (not a real photo). */
export function isFallback(article) {
  return !article?.image_url;
}
